import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, {
    EmblaCarouselType,
    EmblaOptionsType,
} from 'embla-carousel'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import ClassNames from 'embla-carousel-class-names'
import AutoHeight from 'embla-carousel-auto-height'
import Autoplay, { AutoplayType } from "embla-carousel-autoplay"

export class EmblaSlider extends UcoastEl {
    static htmlSelector = 'embla-slider'
    emblaApi?: EmblaCarouselType
    private autoplayInstance?: AutoplayType
    private dots: HTMLButtonElement[] = []
    private thumbButtons: HTMLButtonElement[] = []
    private options?: EmblaOptionsType
    private desktopLayout = window.matchMedia('(min-width: 750px)')
    private onLayoutChange = () => this.refreshResponsiveLayout()

    constructor() {
        super()
        void this.initializeSlider()
    }

    override connectedCallback() {
        super.connectedCallback()
        if (this.dataset.responsiveSlider === 'true') {
            this.desktopLayout.addEventListener('change', this.onLayoutChange)
        }
    }

    async initializeSlider() {
        const OPTIONS: EmblaOptionsType = JSON.parse(q.ra(this, 'data-slider-settings'))
        this.options = OPTIONS

        const viewportNode = q.rs('.embla__viewport', this)
        try {
            const plugins = [
                WheelGesturesPlugin(),
                ClassNames(),
                ...(this.dataset.responsiveSlider === 'true' ? [] : [AutoHeight()]),
                ...(this.dataset.autoplay === 'true' ? [Autoplay({delay: this.dataset.delay ? parseInt(this.dataset.delay) : 0, stopOnInteraction: false})] : [])
            ]

            this.emblaApi = EmblaCarousel(viewportNode, this.getResponsiveOptions(), plugins)

            if(this.dataset.autoplay === 'true') {
                this.autoplayInstance = plugins.find((plugin) => typeof plugin === 'object' && plugin && 'reset' in plugin) as AutoplayType | undefined
            }

            if (this.emblaApi) {
                if(this.querySelector('.embla__dots')) {
                    const emblaDots = q.rs('.embla__dots', this)
                    this.setupDots(emblaDots)
                }

                if (this.querySelectorAll('[data-embla-thumb-index]').length) {
                    this.setupThumbs()
                }

                if (this.querySelector('[data-gallery-current]')) {
                    this.setupProgress()
                }

                if(this.querySelector('.embla__scrollbar')) {
                    const scrollbarNode = q.rs('.embla__scrollbar', this)
                    this.setupScrollbar(scrollbarNode)
                }

                this.querySelector('.embla__prev')?.addEventListener('click', this.scrollPrev.bind(this))
                this.querySelector('.embla__next')?.addEventListener('click', this.scrollNext.bind(this))

                if(!this.hasAttribute('data-scroll-flexible')) this.emblaApi.on('scroll', this.onScroll.bind(this))
            }
        } catch (e) {
            console.error(e)
        }
    }

    override disconnectedCallback() {
        if (this.dataset.responsiveSlider === 'true') {
            this.desktopLayout.removeEventListener('change', this.onLayoutChange)
        }
        super.disconnectedCallback()
    }

    refreshResponsiveLayout() {
        if (!this.emblaApi || !this.options) return
        this.emblaApi.reInit(this.getResponsiveOptions())
    }

    private getResponsiveOptions(): EmblaOptionsType {
        if (!this.options || this.dataset.responsiveSlider !== 'true') {
            return this.options || {}
        }

        const sliderEnabled = this.desktopLayout.matches
            ? this.dataset.desktopSlider === 'true'
            : this.dataset.mobileSlider === 'true'

        return {
            ...this.options,
            watchDrag: sliderEnabled
                ? (_emblaApi, event) => {
                    const target = event.target
                    const nestedSlider = target instanceof Element
                        ? target.closest<EmblaSlider>('embla-slider')
                        : null
                    if (!nestedSlider || nestedSlider === this) return true

                    return (nestedSlider.emblaApi?.scrollSnapList().length || 0) <= 1
                }
                : false,
        }
    }

    private setupDots(emblaDots: HTMLElement) {
        const slides = this.emblaApi?.slideNodes() || []
        const slideShow = this.dataset.slideShow || 1
        if(Number(slideShow) >= slides.length) return
        const createDots = () => {
            emblaDots.innerHTML = ''
            this.dots = []
            
            this.dots = Array.from({ length: slides.length }).map((_, index) => {
                const dot = document.createElement('button')
                dot.className = 'embla__dot'
                dot.innerHTML = `<span class="visually-hidden">${index}</span>`
                dot.addEventListener('click', () => this.emblaApi?.scrollTo(index))
                emblaDots.appendChild(dot)
                return dot
            })
        }

        const updateDots = () => {
            const selectedIndex = this.emblaApi?.selectedScrollSnap() || 0
            this.dots.forEach((dot, index) => {
                dot.classList.toggle('embla__dot--selected', index === selectedIndex)
            })
        }

        createDots()
        updateDots()

        this.emblaApi?.on('select', updateDots)
    }

    private setupThumbs() {
        this.thumbButtons = Array.from(
            this.querySelectorAll<HTMLButtonElement>('[data-embla-thumb-index]')
        )

        const updateThumbs = () => {
            const selectedIndex = this.emblaApi?.selectedScrollSnap() || 0

            this.thumbButtons.forEach((button) => {
                const thumbIndex = Number(button.dataset.emblaThumbIndex)
                const isActive = thumbIndex === selectedIndex
                button.classList.toggle('is-active', isActive)
                if (isActive) {
                    button.setAttribute('aria-current', 'true')
                } else {
                    button.removeAttribute('aria-current')
                }
            })
        }

        this.thumbButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const index = Number(button.dataset.emblaThumbIndex)
                this.emblaApi?.scrollTo(index)
            })
        })

        updateThumbs()
        this.emblaApi?.on('select', updateThumbs)
    }

    private setupProgress() {
        const currentEl = q.os<HTMLElement>('[data-gallery-current]', this)
        const totalEl = q.os<HTMLElement>('[data-gallery-total]', this)
        if (!currentEl || !totalEl) return

        totalEl.textContent = String(this.emblaApi?.slideNodes().length || 0)

        const updateProgress = () => {
            currentEl.textContent = String((this.emblaApi?.selectedScrollSnap() || 0) + 1)
        }

        updateProgress()
        this.emblaApi?.on('select', updateProgress)
    }

    private setupScrollbar(scrollbarNode: HTMLElement) {
        const enableScrollbar = this.dataset.scrollbar ? this.dataset.scrollbar == 'true' : false
        if(enableScrollbar) {
            const scrollbarThumb = q.rs('.embla__scrollbar__thumb', scrollbarNode)
            const slides = this.emblaApi?.slideNodes() || []
            const slideShow = this.dataset.mobileShow || 1
            if(slides.length > Number(slideShow)) {
                this.emblaApi?.on('scroll', () => {
                    const range = scrollbarNode.offsetWidth - scrollbarThumb.offsetWidth
                    const scorllProgress = Number(this.emblaApi?.scrollProgress())
                    const thumbPosition = scorllProgress * range
                    scrollbarThumb.style.transform = `translateX(${thumbPosition}%)`
                })
            } else {
                scrollbarNode.remove()
            }
        } else {
            scrollbarNode.remove()
        }
    }

    onScroll() {
        if(!this.emblaApi) return
        const scrollProgress = this.emblaApi.scrollProgress()
        if (scrollProgress >= 1 || scrollProgress <= 0) {
            this.emblaApi.internalEngine().translate.toggleActive(false)
        } else {
            this.emblaApi.internalEngine().translate.toggleActive(true)
        }
    }

    scrollPrev() {
        this.emblaApi?.scrollPrev()
        if(this.dataset.autoplay === 'true') this.autoplayInstance?.reset()
    }

    scrollNext() {
        this.emblaApi?.scrollNext()
        if(this.dataset.autoplay === 'true') this.autoplayInstance?.reset()
    }

    getSelectedIndex() {
        return this.emblaApi?.selectedScrollSnap() || 0
    }

    scrollMove(index: number) {
        setTimeout(() => {
            this.emblaApi?.scrollTo(index)
        }, 100)
    }
}
